import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { token, user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [incomingMessage, setIncomingMessage] = useState(null);
  const [readReceipts, setReadReceipts] = useState(null);
  const [typingIndicators, setTypingIndicators] = useState({});
  const [unreadCount, setUnreadCount] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!token || !user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const socket = io({
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('receive_message', (message) => {
      setIncomingMessage(message);
    });

    socket.on('typing_indicator', ({ matchId, userId, display_name, isTyping }) => {
      setTypingIndicators((prev) => ({
        ...prev,
        [matchId]: isTyping ? { userId, display_name } : null,
      }));
    });

    socket.on('read_receipt', ({ matchId, messageId, readBy }) => {
      setReadReceipts({ matchId, messageId, readBy, _ts: Date.now() });
    });

    socket.on('user_online', ({ userId }) => {
      setOnlineUsers((prev) => new Set([...prev, userId]));
    });

    socket.on('unread_update', ({ total }) => {
      setUnreadCount(total);
    });

    socket.on('user_offline', ({ userId }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, user]);

  const sendMessage = useCallback((matchId, content) => {
    socketRef.current?.emit('send_message', { matchId, content });
  }, []);

  const sendTypingIndicator = useCallback((matchId, isTyping) => {
    socketRef.current?.emit('typing_indicator', { matchId, isTyping });
  }, []);

  const sendReadReceipt = useCallback((matchId, messageId) => {
    socketRef.current?.emit('read_receipt', { matchId, messageId });
  }, []);

  const joinMatch = useCallback((matchId) => {
    socketRef.current?.emit('join_match', { matchId });
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        onlineUsers,
        incomingMessage,
        readReceipts,
        typingIndicators,
        unreadCount,
        sendMessage,
        sendTypingIndicator,
        sendReadReceipt,
        joinMatch,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}
